/**
 * Conversations — TypeScript contracts.
 *
 * Mirrors `web-merchant-portal-bff`'s `openapi.yml` §13d (Conversations) and §13e (the Customer 360
 * shortcut) field-for-field, as shipped on 2026-09-17 (plan-conversation-003: episodes, attachments,
 * edits/deletions, reply-threading, delivery diagnostics, keyset paging, optimistic episode versions)
 * and updated for the 2026-09-23 delta (adr-conversation-013 D3/D4, adr-merchant-portal-022 D3/D4/D6):
 * per-session reachability on `ConversationSummary` rows, `ConversationStaffRef` (who acknowledged,
 * resolved, closed or replied — resolved by this BFF from the tenant's staff directory), `kind=OPERATOR`
 * recognition, and the `mergedInto` / `OperatorReplyAccepted.conversationId` alias pass-through.
 * Verified against the live contract, not a design fixture — see plan-merchant-portal-019/-020 and
 * design-conversations-inbox.md §13.
 */

export type ConversationChannel = 'TELEGRAM' | 'MAX';

/** The thread-level status — a MIRROR of the current episode; `MERGED` marks an alias of a surviving thread. */
export type ConversationStatus = 'OPEN' | 'WAITING_OPERATOR' | 'OPERATOR' | 'CLOSED' | 'MERGED';

export type ConversationMessageDirection = 'IN' | 'OUT';

export type ConversationMessageKind =
  | 'TEXT'
  | 'COMMAND'
  | 'CALLBACK'
  | 'CONTACT'
  | 'MEDIA'
  | 'LOCATION'
  | 'SYSTEM'
  | 'UNSUPPORTED'
  | 'OPERATOR'
  | 'NOTIFICATION';

export type ConversationHandledBy =
  | 'RULE'
  | 'COMMAND'
  | 'FAQ'
  | 'AGENT'
  | 'OPERATOR'
  | 'NOTIFICATION'
  | 'SYSTEM';

/** `FAILED` means "retryable — the hub re-dispatches"; `PERMANENTLY_FAILED` is terminal. */
export type ConversationDeliveryStatus = 'QUEUED' | 'SENT' | 'FAILED' | 'PERMANENTLY_FAILED';

/**
 * Additive-only on the wire: a reason this app does not know is passed through by the BFF, never
 * rejected — render unknown values as-is rather than failing.
 */
export type ConversationEscalationReason =
  | 'EXPLICIT_REQUEST'
  | 'LOW_CONFIDENCE'
  | 'LOOP_DETECTED'
  | 'POLICY_TOPIC'
  | 'AGENT_ESCALATED'
  | 'AI_UNAVAILABLE'
  | 'NO_TEMPLATE'
  | (string & {});

export type ConversationEscalationStatus = 'REQUESTED' | 'ACKED' | 'RESOLVED' | 'EXPIRED';

export type ConversationEpisodeStatus = 'OPEN' | 'WAITING_OPERATOR' | 'OPERATOR' | 'CLOSED';

export type ConversationEpisodeClosedReason =
  | 'IDLE_TIMEOUT'
  | 'OPERATOR_CLOSED'
  | 'CUSTOMER_DELETED'
  | 'MERGED'
  | 'THREAD_CLOSED';

export type ConversationAttachmentType =
  | 'IMAGE'
  | 'DOCUMENT'
  | 'AUDIO'
  | 'VOICE'
  | 'VIDEO'
  | 'VIDEO_NOTE'
  | 'ANIMATION'
  | 'STICKER'
  | 'UNKNOWN';

export type ConversationAttachmentStorageStatus =
  | 'STORED'
  | 'REJECTED'
  | 'FAILED'
  | 'NOT_STORED'
  | 'PURGED';

/**
 * Attachment types the BFF treats as personal data: their content additionally requires
 * `customers.feature.pii-view` (403 otherwise). `IMAGE`, `ANIMATION`, `STICKER` need only
 * `conversations.view`. Mirrors `ConversationsControllerImpl`'s rule (adr-merchant-portal-021 D3).
 */
export const PII_GATED_ATTACHMENT_TYPES: readonly ConversationAttachmentType[] = [
  'DOCUMENT',
  'AUDIO',
  'VOICE',
  'VIDEO',
  'VIDEO_NOTE',
];

/**
 * A staff member who acted on a conversation, resolved by this BFF from the tenant's Keycloak staff
 * directory (adr-merchant-portal-022 D4) — the hub stores the opaque actor id only. Name only; never
 * e-mail or role. The staff-naming rule that consumes this (`staffLabel` in `./staff.ts`, design §13.2)
 * decides "you" by `actorId` ALONE, before and regardless of `resolution`/`displayName`.
 */
export interface ConversationStaffRef {
  /** The hub's opaque staff actor id — the employee's Keycloak subject; equals `GET /api/v1/me`
   *  `user.id` for the caller's own actions. NEVER rendered directly. */
  actorId: string;
  /** The employee's name in this tenant's staff directory; `null` unless `resolution === 'MEMBER'`
   *  (and even then, when the member has no name on file). */
  displayName?: string | null;
  /**
   * `MEMBER` — a current member of this tenant (active or suspended); `NOT_A_MEMBER` — not, or no
   * longer, a member; `UNAVAILABLE` — the staff directory could not be read for this response (the
   * BFF fails open: a directory failure never fails or slows the conversation read).
   */
  resolution: 'MEMBER' | 'NOT_A_MEMBER' | 'UNAVAILABLE';
}

export interface ConversationEpisodeSummary {
  episodeId: string;
  status: ConversationEpisodeStatus;
  /** ≤ 200 chars; renamed via `PATCH …/episodes/{episodeId}`. */
  topic: string | null;
  openIssueRef: string | null;
  openedByChannel: ConversationChannel;
  startedAt: string;
  lastActivityAt: string;
  closedAt: string | null;
  closedReason: ConversationEpisodeClosedReason | null;
  /** Derived at read time from `idleGraceMinutes`; never stored. */
  idle: boolean;
  /** Optimistic lock — echo on `:close`, `PATCH …/escalations/{id}` and `PATCH …/episodes/{id}`. */
  version: number;
  /**
   * The staff actor (opaque id) who closed the episode (`closedReason === 'OPERATOR_CLOSED'`); `null`
   * for every system close (`IDLE_TIMEOUT`, `CUSTOMER_DELETED`, `MERGED`, `THREAD_CLOSED`). Passed
   * through from the hub unchanged (adr-conversation-013 D4).
   */
  closedBy?: string | null;
  /** The staff member named by `closedBy`, resolved by this BFF (adr-merchant-portal-022 D4). */
  closedByRef?: ConversationStaffRef | null;
}

export interface ConversationEpisodePage {
  items: ConversationEpisodeSummary[];
  page: number;
  size: number;
  total: number;
}

export interface PatchConversationEpisodeRequest {
  topic: string | null;
  /** The episode version last read; a stale value is rejected with 409 `settings.version_conflict`. */
  version: number;
}

export interface ConversationMessagePart {
  type: 'TEXT' | 'IMAGE' | 'DOCUMENT' | 'LOCATION';
  text?: string | null;
  mediaRef?: string | null;
  format: 'PLAIN' | 'MARKDOWN' | 'HTML';
}

export interface ConversationMessageActionItem {
  type: 'BUTTON' | 'QUICK_REPLY' | 'URL' | 'DEEP_LINK' | 'REQUEST_CONTACT';
  label: string;
  value: string;
  row?: number | null;
}

export interface ConversationEscalation {
  id: string;
  reason: ConversationEscalationReason;
  status: ConversationEscalationStatus;
  summary?: string | null;
  requestedAt: string;
  expiresAt?: string | null;
  ackedAt?: string | null;
  ackedBy?: string | null;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  /** The staff member named by `ackedBy`, resolved by this BFF (adr-merchant-portal-022 D4). */
  ackedByRef?: ConversationStaffRef | null;
  /** The staff member named by `resolvedBy`, resolved by this BFF (adr-merchant-portal-022 D4). */
  resolvedByRef?: ConversationStaffRef | null;
}

export interface ConversationSummary {
  id: string;
  /** `null` while the thread is provisional (no known customer yet). */
  customerId: string | null;
  provisional?: boolean;
  status: ConversationStatus;
  lastChannel: ConversationChannel;
  lastInboundAt?: string | null;
  lastOutboundAt?: string | null;
  language?: string | null;
  /** Masked to `null` unless the caller holds `customers.feature.pii-view`. */
  displayName?: string | null;
  openEscalation?: ConversationEscalation | null;
  lastMessagePreview?: string | null;
  /** The thread's current (non-CLOSED) episode, or `null` when none is active. */
  currentEpisode?: ConversationEpisodeSummary | null;
  /**
   * Every channel session of the thread with its reachability, most recently active first — passed
   * through from the hub on LIST rows too as of adr-conversation-013 D3 (design §13.1). Not masked (no
   * customer-typed content). Absent or empty on an older BFF — the row then falls back to the single
   * `lastChannel` badge.
   */
  sessions?: ConversationSessionView[];
}

export interface ConversationSessionView {
  id: string;
  channel: ConversationChannel;
  botId?: string | null;
  /** `false` once the customer blocked the bot — an operator reply on this channel will not arrive. */
  reachable: boolean;
  locale?: string | null;
  lastActivityAt?: string | null;
}

export interface ConversationDetail extends ConversationSummary {
  contextSummary?: string | null;
  contextSummaryAt?: string | null;
  unresolvedTurns?: number | null;
  episodesCount?: number | null;
  /** Wire-identical to the inherited `ConversationSummary.sessions` — always present on the detail. */
  sessions: ConversationSessionView[];
  /**
   * Set when the requested id is a MERGED alias: the detail is the SURVIVING thread's and this value
   * repeats its own `id`, so the caller can update its reference. `null` when the requested id is the
   * thread itself (adr-merchant-portal-022 D3/D6, design §13.5). Prefer this over the `id`-comparison
   * fallback (`mergedAliasTarget`) when present.
   */
  mergedInto?: string | null;
}

export interface ConversationMessageAttachment {
  attachmentId: string;
  type: ConversationAttachmentType;
  mimeType?: string | null;
  sizeBytes?: number | null;
  /** Personal data by the same rule as `text` — masked to `null` on inbound rows without pii-view. */
  fileName?: string | null;
  durationSeconds?: number | null;
  storageStatus: ConversationAttachmentStorageStatus;
  /**
   * This BFF's own streaming path (`/api/v1/conversations/{id}/attachments/{attachmentId}/content`) when
   * `storageStatus=STORED`, else `null`. Never a storage URL — load it under the session cookie as-is.
   */
  contentUrl?: string | null;
}

export interface ConversationMessageView {
  id: string;
  direction: ConversationMessageDirection;
  kind: ConversationMessageKind;
  channel: ConversationChannel;
  /** Masked to `null` on inbound messages when the caller lacks `customers.feature.pii-view`. */
  text?: string | null;
  parts?: ConversationMessagePart[];
  actions?: ConversationMessageActionItem[];
  /** Outbound rows only. */
  deliveryStatus?: ConversationDeliveryStatus | null;
  /** Adapter vocabulary (`RATE_LIMITED`, `BOT_BLOCKED`, …) or the hub's `RETRIES_EXHAUSTED`/`EXPIRED`. Outbound only. */
  deliveryReason?: string | null;
  /** How many retryable `FAILED` outcomes this reply has had. Outbound only. */
  deliveryAttempts?: number | null;
  handledBy?: ConversationHandledBy | null;
  intentCode?: string | null;
  confidence?: number | null;
  latencyMs?: number | null;
  createdAt: string;
  episodeId?: string | null;
  /** The message this one answers (internal id), when known. */
  replyToMessageId?: string | null;
  replyToChannelMessageId?: string | null;
  channelMessageIds?: string[];
  edited?: boolean | null;
  /** The customer's corrected text — masked exactly like `text`. */
  editedText?: string | null;
  /** `true` when the customer deleted the message; `text` is then `null`. */
  deleted?: boolean | null;
  attachments?: ConversationMessageAttachment[];
  /**
   * The operator who sent this reply (opaque id), passed through from the hub (adr-conversation-013
   * D4); `null` on every other row. An operator reply is `kind=OPERATOR`, but rows written before the
   * hub's backfill still carry `kind=TEXT` with `handledBy=OPERATOR` — see `isOperatorRow` in
   * `./messageDisplay.ts`, which recognises both.
   */
  operatorActorId?: string | null;
  /** Set when `operatorActorId` is — resolved by this BFF (adr-merchant-portal-022 D4). */
  operator?: ConversationStaffRef | null;
}

export interface ConversationPage {
  content: ConversationSummary[];
  page: number;
  size: number;
  totalElements: number;
}

export interface ConversationMessagePage {
  content: ConversationMessageView[];
  /** Keyset cursor for the next OLDER page; `null` when the start of the thread is reached. */
  nextBefore?: string | null;
}

export interface ListConversationsParams {
  status?: ConversationStatus;
  channel?: ConversationChannel;
  customerId?: string;
  updatedAfter?: string;
  /** 0-based. */
  page?: number;
  /** 1–200, default 20. */
  size?: number;
}

export interface ListConversationMessagesParams {
  /** Keyset cursor — messages created strictly before this instant. */
  before?: string;
  /** 1–200, default 50. */
  size?: number;
  direction?: ConversationMessageDirection;
}

export interface ListConversationEpisodesParams {
  status?: ConversationEpisodeStatus;
  page?: number;
  size?: number;
}

export interface OperatorReplyRequest {
  /** Client-minted idempotency key (UUID). Re-posting the same one sends nothing twice. */
  replyId: string;
  /** At least one part. */
  parts: ConversationMessagePart[];
  actions?: ConversationMessageActionItem[];
  closeConversation?: boolean;
  /**
   * Optional. Defaults to the channel of the customer's LAST INBOUND message — the chat they are
   * writing in (design §13.4; corrected from the earlier `currentEpisode.openedByChannel` assumption).
   * A channel with no session is `409 SESSION_NOT_FOUND`; a session the customer blocked the bot on is
   * `409 SESSION_UNREACHABLE` — never a silent fallback to another channel.
   */
  channel?: ConversationChannel | null;
  /** Optional — the inbound message this reply answers; the hub quotes it on the origin channel. */
  replyToMessageId?: string | null;
}

/** `202` — accepted for delivery, not sent: the outcome arrives later on the timeline. */
export interface OperatorReplyAccepted {
  messageId: string;
  conversationStatus: ConversationStatus;
  /**
   * The thread the reply was actually written to — the SURVIVOR when the requested id was a MERGED
   * alias (adr-conversation-013 D7, adr-merchant-portal-022 D6, design §13.5). Absent/equal to the
   * requested id on the normal (non-merged) path.
   */
  conversationId?: string;
}

export interface PatchEscalationRequest {
  status: 'ACKED' | 'RESOLVED';
  /** Optional — the owning episode's `version` last read; a stale value is rejected with 409. */
  version?: number | null;
}

export interface CloseConversationRequest {
  /** Optional — the current episode's `version` last read; a stale value is rejected with 409. */
  version?: number | null;
}

/**
 * The `409` codes of `POST …/operator-replies` (BFF `ApiError.code`). `SESSION_UNREACHABLE` carries
 * `details.reachableChannels` — channels still reachable, possibly empty.
 */
export const OPERATOR_REPLY_CONFLICT_CODES = {
  sessionNotFound: 'SESSION_NOT_FOUND',
  sessionUnreachable: 'SESSION_UNREACHABLE',
} as const;

/** The `409` code of `:close`, `PATCH …/escalations/{id}` and `PATCH …/episodes/{id}` on a stale episode `version`. */
export const VERSION_CONFLICT_CODE = 'settings.version_conflict';

/** The `409` code of `PATCH …/escalations/{id}` when the transition is not allowed from the current status. */
export const ESCALATION_ILLEGAL_TRANSITION_CODE = 'escalation.illegal_transition';
